/**
 * LiveClassroomList — schedule board for teachers + joinable list for students.
 *
 * Phase 1 intentionally ships without native video; teachers attach an external
 * meeting URL (Google Meet / Zoom / YouTube Live / Jitsi). The wrapper still
 * tracks attendance, chat, hand-raise, materials independently.
 *
 * Uses existing school_lms live-classes endpoints (schema: scheduled_at ISO
 * string, duration_minutes, meeting_url, meeting_platform, class_ids[]).
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Radio, Video, Plus, PlayCircle, Square, Users, Clock, ExternalLink, Loader2, ArrowLeft, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import LectureSearch from '@/components/school/LectureSearch';
import StudyClips from '@/components/school/StudyClips';
import TranscriptionPanel from '@/components/school/TranscriptionPanel';
import InboxNotices from '@/components/school/InboxNotices';
import CatchUpStreak from '@/components/school/CatchUpStreak';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = Array.isArray(err.detail)
      ? err.detail.map((d) => d.msg || String(d)).join('; ')
      : err.detail;
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const statusColor = (s) => ({
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  live: 'bg-emerald-50 text-emerald-700 border-emerald-200 animate-pulse',
  completed: 'bg-slate-50 text-slate-600 border-slate-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
}[s] || 'bg-slate-50 text-slate-600 border-slate-200');

export default function LiveClassroomList() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [linkFor, setLinkFor] = useState(null);

  // Detect portal role — students & parents are read-only.
  const { data: me } = useQuery({
    queryKey: ['portal-me'],
    queryFn: () => api('/school/portal/me').catch(() => null),
    retry: false,
    refetchOnWindowFocus: false,
  });
  // Until we know who this is, assume the least: a student should never see a
  // Schedule button flash, however briefly.
  const userType = me?.user_type || '';
  const canSchedule = userType === 'teacher' || userType === 'admin';
  const canCancel = canSchedule;
  const canChangeStatus = canSchedule;

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['live-classes', tenantId],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes`),
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/school/tenants/${tenantId}/classes`).catch(() => []),
  });
  const { data: subjects = [] } = useQuery({
    queryKey: ['school-subjects', tenantId],
    queryFn: () => api(`/school/tenants/${tenantId}/subjects`).catch(() => []),
  });
  const { data: teachers = [] } = useQuery({
    queryKey: ['school-teachers', tenantId],
    queryFn: () => api(`/school/tenants/${tenantId}/teachers`).catch(() => []),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => api(`/school/tenants/${tenantId}/live-classes/${id}/status?status=${status}`, { method: 'PUT' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['live-classes', tenantId] }); },
    onError: (e) => toast.error(e.message),
  });
  const cancelMut = useMutation({
    mutationFn: (id) => api(`/school/tenants/${tenantId}/live-classes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Cancelled'); qc.invalidateQueries({ queryKey: ['live-classes', tenantId] }); },
    onError: (e) => toast.error(e.message),
  });

  const grouped = useMemo(() => {
    const out = { live: [], scheduled: [], completed: [], cancelled: [] };
    for (const s of sessions) (out[s.status] || out.scheduled).push(s);
    return out;
  }, [sessions]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="live-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
                <Video className="h-7 w-7 text-indigo-600" /> Live Classroom
              </h1>
              <p className="text-sm text-slate-500">Schedule, start, and run live sessions with attendance & chat.</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="new-live-session-btn" className={canSchedule ? '' : 'hidden'}>
            <Plus className="h-4 w-4 mr-1" /> Schedule
          </Button>
        </div>

        <InboxNotices tenantId={tenantId} />
        <CatchUpStreak tenantId={tenantId} />
        <TranscriptionPanel tenantId={tenantId} />
        <LectureSearch tenantId={tenantId} />
        <StudyClips tenantId={tenantId} />

        {isLoading && (
          <div className="py-16 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading sessions…</div>
        )}

        {!isLoading && sessions.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Radio className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">
                {canSchedule
                  ? 'No live sessions yet. Schedule your first class to get started.'
                  : 'No live sessions yet. Your teacher will schedule classes here.'}
              </p>
            </CardContent>
          </Card>
        )}

        {!isLoading && Object.entries(grouped).map(([status, list]) =>
          list.length === 0 ? null : (
            <div key={status} className="mb-8">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">
                {status === 'live' ? 'Live now' : status === 'scheduled' ? 'Upcoming' : status === 'completed' ? 'Past' : 'Cancelled'}
                <span className="ml-2 text-slate-400 font-normal">· {list.length}</span>
              </h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {list.map((s) => (
                  <Card key={s.id} data-testid={`live-session-card-${s.id}`} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-base leading-tight line-clamp-2">{s.title}</CardTitle>
                        <Badge variant="outline" className={statusColor(s.status)}>{s.status}</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                        <div>{s.subject_name || 'General'}{s.teacher_name ? ` · ${s.teacher_name}` : ''}</div>
                        <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(s.scheduled_at)} · {s.duration_minutes}min</div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                        <Users className="h-3 w-3" /> {(s.class_ids || []).length} class{(s.class_ids || []).length === 1 ? '' : 'es'}
                        {s.meeting_ready && s.meeting_label && <span className="ml-auto uppercase text-[10px] text-slate-400">{s.meeting_label}</span>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {s.meeting_ready && !['cancelled', 'completed'].includes(s.status) && (
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid={`join-meeting-btn-${s.id}`}
                            onClick={() => window.open(s.join_url, '_blank', 'noopener')}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" /> Join {s.meeting_label}
                          </Button>
                        )}
                        {canChangeStatus && s.status === 'scheduled' && (
                          <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: s.id, status: 'live' })} data-testid={`start-btn-${s.id}`}>
                            <PlayCircle className="h-4 w-4 mr-1" /> Start
                          </Button>
                        )}
                        {canChangeStatus && s.status === 'live' && (
                          <Button size="sm" variant="destructive" onClick={() => setStatus.mutate({ id: s.id, status: 'completed' })} data-testid={`end-btn-${s.id}`}>
                            <Square className="h-4 w-4 mr-1" /> End
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => navigate(`/school/${tenantId}/live-classes/${s.id}`)} data-testid={`enter-btn-${s.id}`}>
                          {canSchedule ? 'Enter room' : 'Join room'}
                        </Button>
                        {canSchedule && s.status !== 'completed' && (
                          <Button size="sm" variant="ghost" className="text-indigo-600" onClick={() => setLinkFor(s)} data-testid={`meeting-link-btn-${s.id}`}>
                            <LinkIcon className="h-3.5 w-3.5 mr-1" /> {s.meeting_ready ? 'Change link' : 'Add link'}
                          </Button>
                        )}
                        {!s.meeting_ready && !canSchedule && s.status !== 'completed' && (
                          <span className="text-xs text-slate-400 self-center" data-testid={`no-link-${s.id}`}>
                            No meeting link yet
                          </span>
                        )}
                        {canCancel && s.status === 'scheduled' && (
                          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => { if (window.confirm('Cancel this session?')) cancelMut.mutate(s.id); }}>
                            Cancel
                          </Button>
                        )}
                      </div>
                      {s.meeting_id && (
                        <p className="text-[11px] text-slate-400 mt-2" data-testid={`meeting-id-${s.id}`}>Meeting ID {s.meeting_id}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )
        )}

        <CreateSessionDialog
          open={showCreate && canSchedule}
          onClose={() => setShowCreate(false)}
          tenantId={tenantId}
          classes={classes}
          subjects={subjects}
          teachers={teachers}
          onCreated={() => { qc.invalidateQueries({ queryKey: ['live-classes', tenantId] }); setShowCreate(false); }}
        />

        <MeetingLinkDialog
          session={linkFor}
          tenantId={tenantId}
          onClose={() => setLinkFor(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['live-classes', tenantId] }); setLinkFor(null); }}
        />
      </div>
    </div>
  );
}

function MeetingLinkDialog({ session, tenantId, onClose, onSaved }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const open = Boolean(session);

  React.useEffect(() => { setUrl(session?.meeting_url || ''); }, [session]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api(`/school/tenants/${tenantId}/live-classes/${session.id}/meeting`, {
        method: 'PUT',
        body: JSON.stringify({ meeting_url: url }),
      });
      toast.success(res.meeting_url ? `${res.meeting_label} link saved — students can join in one tap` : 'Meeting link removed');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="meeting-link-dialog">
        <DialogHeader>
          <DialogTitle>Meeting link</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="meeting-link-input">Paste the Zoom (or Meet / Teams) join link</Label>
          <Input
            id="meeting-link-input"
            data-testid="meeting-link-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://us02web.zoom.us/j/8412345678?pwd=…"
          />
          <p className="text-xs text-slate-500">
            Paste it whole — the passcode in the link is what lets a student join without typing anything.
            Leave empty to remove the link.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-meeting-link-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateSessionDialog({ open, onClose, tenantId, classes, subjects, teachers, onCreated }) {
  const [form, setForm] = useState({
    title: '', subject_id: '', class_ids: [], teacher_id: '',
    scheduled_at: '', duration_minutes: 45,
    meeting_url: '', meeting_platform: 'meet', description: '',
  });
  const [busy, setBusy] = useState(false);

  const reset = () => setForm({
    title: '', subject_id: '', class_ids: [], teacher_id: '',
    scheduled_at: '', duration_minutes: 45,
    meeting_url: '', meeting_platform: 'meet', description: '',
  });

  const save = async () => {
    if (!form.title || !form.subject_id || !form.teacher_id || !form.scheduled_at || form.class_ids.length === 0) {
      toast.error('Title, subject, teacher, class, and start are required');
      return;
    }
    setBusy(true);
    try {
      await api(`/school/tenants/${tenantId}/live-classes`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          scheduled_at: new Date(form.scheduled_at).toISOString(),
          duration_minutes: Number(form.duration_minutes) || 45,
        }),
      });
      toast.success('Session scheduled');
      reset();
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleClass = (cid) => {
    setForm((f) => ({ ...f, class_ids: f.class_ids.includes(cid) ? f.class_ids.filter((x) => x !== cid) : [...f.class_ids, cid] }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-session-dialog">
        <DialogHeader>
          <DialogTitle>Schedule a live class</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
          <div>
            <Label htmlFor="live-title">Title</Label>
            <Input id="live-title" data-testid="live-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Grade 8 Math — Quadratics" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Subject</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="live-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => <SelectItem key={s.id || s._id} value={s.id || s._id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teacher</Label>
              <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                <SelectTrigger data-testid="live-teacher-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                <SelectContent>
                  {teachers.map((t) => <SelectItem key={t.id || t._id} value={t.id || t._id}>{t.first_name} {t.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Classes (pick at least one)</Label>
            <div className="border rounded-md p-2 max-h-32 overflow-auto space-y-1">
              {classes.length === 0 && <p className="text-xs text-slate-400">No classes available.</p>}
              {classes.map((c) => (
                <label key={c.id || c._id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 rounded">
                  <input type="checkbox" checked={form.class_ids.includes(c.id || c._id)} onChange={() => toggleClass(c.id || c._id)} data-testid={`live-class-check-${c.id || c._id}`} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start</Label>
              <Input type="datetime-local" data-testid="live-start-input" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" min={5} max={240} data-testid="live-duration-input" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Meeting URL (optional)</Label>
              <Input placeholder="https://meet.google.com/..." value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} />
            </div>
            <div>
              <Label>Platform</Label>
              <Select value={form.meeting_platform} onValueChange={(v) => setForm({ ...form, meeting_platform: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="meet">Google Meet</SelectItem>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="teams">Teams</SelectItem>
                  <SelectItem value="jitsi">Jitsi</SelectItem>
                  <SelectItem value="youtube">YouTube Live</SelectItem>
                  <SelectItem value="custom">Custom / Internal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Agenda, materials, what students should bring…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-live-session-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
